import { useCallback, useMemo } from 'react';

import { useEsitiSfide, type AnnotaSfida, type Nota } from '@/hooks/useRispostaSfida';
import { useSfideInArrivo } from '@/hooks/useSfideInArrivo';
import { nomeDelGiocoDellaSfida } from '@/lib/limulo/challenge';
import { gameByCode, labelOf } from '@/lib/limulo/profiles';
import { gruppiConTetto, sfideDiAmico, sfideSenzaConversazione, type GruppoDm } from '@/lib/limulo/sfideNelDm';
import { getMyLogtoSub } from '@/lib/logto';
import { useGetLimuloGamesQuery } from '@/services/limuloApi';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **Le sfide ricevute che Amici › Messaggi mostra** (T2-5, lotto B3a). Non legge la rete: prende le sfide dalla
 * cache della barra (`useSfideInArrivo`, gli stessi argomenti, NESSUN ritmo proprio ⇒ zero GET a `/v1/challenges`
 * in piu') e ne sceglie alcune. Barra, tab «Sfide», elenco e conversazione sono quattro finestre sulla STESSA
 * lista, con la stessa `gestite` e la stessa guardia in volo: Accetta o Rifiuta da una qualunque le aggiorna tutte.
 *
 * `vive` sono le carte ancora offerte; `soloEsiti` sono le sfide su cui il giocatore ha appena agito e che la lista
 * ha gia' tolto, tenute `AVVISO_MS` col loro esito (`useEsitiSfide`: senza, un'accettazione riuscita sparirebbe muta).
 * `ce` e' falso fuori serratura (la lettura non parte, `useSfideInArrivo` da' `[]`): chi lo legge non monta niente.
 *
 * ⚠️ Questo hook NON tocca RTK a parte `useSfideInArrivo` (a flag spento una costante): le due schermate DM lo chiamano
 * SEMPRE, anche fuori serratura e nei banchi che non hanno `limuloApi` nello store (`dm-elenco-swipe`). Il catalogo dei
 * giochi vive in `useNomeGiocoDelleSfide`, chiamato dal componente che si monta SOLO se c'e' almeno una carta.
 */
export interface SfideNelDm {
  /** Le carte per persona, gia' col tetto (`gruppiConTetto`): al massimo DUE per chi sfida, e `altre` per il resto. */
  gruppi: GruppoDm<Nota>[];
  note: Readonly<Record<string, Nota>>;
  annota: AnnotaSfida;
  togli: (challengeId: string) => void;
  ce: boolean;
}

/** `scegli` DEVE avere un'identita' stabile fra i render (il chiamante la tiene in un `useCallback`). */
function useSfideScelte(scegli: (vive: readonly LimuloChallengeWithIdentities[]) => LimuloChallengeWithIdentities[]): SfideNelDm {
  const { vive: tutte } = useSfideInArrivo();
  const vive = useMemo(() => scegli(tutte), [scegli, tutte]);
  const viveIds = useMemo(() => new Set(vive.map((c) => c.challengeId)), [vive]);
  const { note, annota, togli } = useEsitiSfide(viveIds);
  // ⚠️ Anche gli ESITI passano da `scegli`: la schermata resta montata quando cambia `:conversationId`, e l'esito (o l'errore)
  // di una sfida di A non deve comparire nella conversazione di B (misurato dal revisore di B3a: accetto in /messaggi/a,
  // navigo a /messaggi/b, e in b compariva la carta di A con «sfida_accetta_ok» per fino a 5 s).
  const soloEsiti = useMemo(
    () =>
      Object.values(note).filter(
        (n) => (n.tipo !== 'errore' || !viveIds.has(n.sfida.challengeId)) && scegli([n.sfida]).length > 0,
      ),
    [note, viveIds, scegli],
  );
  const gruppi = useMemo(
    () => gruppiConTetto(soloEsiti.map((n) => ({ sfida: n.sfida, nota: n })), vive),
    [soloEsiti, vive],
  );
  return { gruppi, note, annota, togli, ce: gruppi.length > 0 };
}

/**
 * Il nome del gioco di ogni sfida mostrata: `gameName` del BFF se c'e' (nessuna GET), altrimenti il catalogo `/v1/games`
 * — che si legge SOLO se almeno una sfida mostrata non porta il nome — e in ultimo il `gameId`. Si chiama da un
 * componente montato solo con almeno una carta: e' l'unico punto in cui questa superficie tocca `limuloApi` per il catalogo.
 */
export function useNomeGiocoDelleSfide(
  mostrate: readonly LimuloChallengeWithIdentities[],
): (c: LimuloChallengeWithIdentities) => string {
  const serveIlCatalogo = mostrate.some((c) => (c.gameName ?? null) === null);
  const giochi = useGetLimuloGamesQuery(undefined, { skip: !serveIlCatalogo });
  const catalogo = giochi.data;
  return useCallback(
    (c) => nomeDelGiocoDellaSfida(c, (gameId) => labelOf(gameByCode(gameId, catalogo ?? []), gameId)),
    [catalogo],
  );
}

/** La conversazione con `friendUserId`: le sfide che quella persona ti ha fatto. */
export function useSfideDiAmico(friendUserId: string | undefined): SfideNelDm {
  const mio = getMyLogtoSub();
  const scegli = useCallback(
    (vive: readonly LimuloChallengeWithIdentities[]) => sfideDiAmico(vive, friendUserId, mio),
    [friendUserId, mio],
  );
  return useSfideScelte(scegli);
}

/** L'elenco: le sfide di chi non ha una conversazione utilizzabile (`null` = elenco non arrivato ⇒ nessuna). */
export function useSfideSenzaConversazione(conConversazione: ReadonlySet<string> | null): SfideNelDm {
  const mio = getMyLogtoSub();
  const scegli = useCallback(
    (vive: readonly LimuloChallengeWithIdentities[]) => sfideSenzaConversazione(vive, conConversazione, mio),
    [conConversazione, mio],
  );
  return useSfideScelte(scegli);
}
